/**
 * Achievement System Types
 * Based on original Marune app's perks and goals system
 */

// Perks System Types
export interface Perk {
  key: string;
  title: string;
  teaser: string;
  description: string; // Can contain HTML
  partnerLink: {
    url: string;
    callToAction: string;
  };
  imageURL: string;
  orderIndex: number;
  isActive: boolean;
  createdAt: number;
  validUntil?: number;
}

export interface PerkReward {
  perkCode: string;
  perkCodeDescription: string;
}

export interface UnlockedPerk {
  perkKey: string;
  unlockedAt: number;
  hasSeenReward?: boolean;
  copiedCode?: boolean;
}

// Goals System Types
export type GoalType = 'time' | 'consistency' | 'weight';
export type GoalFrequency = 'weekly' | 'monthly';

export interface Goal {
  id?: string;
  type: GoalType;
  frequency: GoalFrequency;
  um: string; // Unit of measurement (e.g., "hours", "times", "kg")
  umShort: string; // Short unit (e.g., "h", "x", "kg")
  isActive: boolean;
  targetValue: number;
  dateUpdated: number;
  createdAt?: number;
}

export interface GoalProgress {
  goalId: string;
  period: string; // Format: "YYYY-W" for weekly, "YYYY-M" for monthly
  currentValue: number;
  targetValue: number;
  lastUpdated: number;
  achieved: boolean;
}

export interface GoalStats {
  progress: number;
  achieved: boolean;
  lastTraining?: number | null;
  lastUpdate?: number;
}

export interface GoalStatsMap {
  [period: string]: GoalStats;
}

// Public goal templates
export interface PublicGoal extends Goal {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
}

// Achievement tracking
export interface AchievementTrigger {
  type: 'training_count' | 'friend_count' | 'streak' | 'goal_complete' | 'manual';
  condition: any;
  perkKey?: string;
  notification?: {
    title: string;
    body: string;
  };
}

// Perk unlock conditions
export const PERK_UNLOCK_CONDITIONS = {
  TEN_FOR_TEN: {
    type: 'friend_count' as const,
    minFriends: 10,
  },
  NEW_USER_PERKS: [
    'Habrok',
    'Takedown', 
    'Cocoa_375_Off',
    'BattleBalm',
    'BattleBalmCBD',
  ],
} as const;

// Goal icons and colors
export const GOAL_STYLES = {
  time: {
    icon: 'clock',
    color: '#4CAF50', // Green
  },
  consistency: {
    icon: 'calendar-check',
    color: '#9C27B0', // Purple
  },
  weight: {
    icon: 'weight',
    color: '#FF9800', // Orange
  },
} as const;